import React, { useEffect, useRef, useState } from 'react';
import { CheckCircleIcon } from 'lucide-react';
import { CRESTMONT } from './CrestmontChrome';
import { getUniversityBySlug } from '../../data/universityData';
import { getPublisherParams } from '../../utils/publisherParams';
import { getMetaCookies, resolveSub3 } from '../../utils/metaCookies';
import { trackFormSubmit } from '../../utils/trackFormSubmit';

// Compact single-panel request form used on the Crestmont program pages.
// The multi-step advertiser template is the right shape for a cold visitor;
// on a program page the visitor already knows which degree they want, so the
// program is pre-selected and the form asks only for what the lead requires.

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
}
function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
interface CrestmontLeadFormProps {
  /** Pre-selected program, posted as mProgram. */
  programValue: string;
  programName: string;
  /** Other Crestmont degrees the visitor can switch to. */
  programOptions: {
    label: string;
    value: string;
  }[];
  heading?: string;
  subheading?: string;
}
export function CrestmontLeadForm({
  programValue,
  programName,
  programOptions,
  heading = 'Request Program Information',
  subheading
}: CrestmontLeadFormProps) {
  const university = getUniversityBySlug('crestmont-university');
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current) sessionIdRef.current = `s-${generateUUID()}`;
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    program: programValue,
    zipCode: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Keep the dropdown in sync when the visitor moves between program pages.
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      program: programValue
    }));
  }, [programValue]);

  // LeadiD + TrustedForm consent certification. Both scripts write into the
  // hidden inputs rendered below, which must stay mounted while the form is.
  useEffect(() => {
    ;
    (window as any).myCallbackFunction = function (token: string) {
      const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
      if (field) field.setAttribute('value', token);
    };
    const leadIdScript = document.createElement('script');
    leadIdScript.id = 'LeadiDscript_campaign';
    leadIdScript.type = 'text/javascript';
    leadIdScript.async = true;
    leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
    document.body.appendChild(leadIdScript);
    const tfScript = document.createElement('script');
    tfScript.type = 'text/javascript';
    tfScript.async = true;
    tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape('false') + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
    document.body.appendChild(tfScript);
    return () => {
      try {
        leadIdScript.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  const update = (field: keyof typeof form, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError('');
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (form.zipCode.replace(/\D/g, '').length !== 5) {
      setError('Please enter a valid 5-digit ZIP code.');
      return;
    }
    setIsSubmitting(true);
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const payload: Record<string, string> = {
      ...getPublisherParams(window.location.search, {
        mPubID: university?.mPubID || '',
        mPubCampaignID: university?.mPubCampaignID || ''
      }),
      mBrandID: university?.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: sessionIdRef.current,
      leadIdToken,
      trustedFormUrl: tfInput?.value || '',
      mProgram: form.program,
      degreeInterest: '',
      areaStudy: '',
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      phone: form.phone.replace(/\D/g, ''),
      zipCode: form.zipCode.replace(/\D/g, '').slice(0, 5),
      useragent: navigator.userAgent,
      sub3: resolveSub3(window.location.search),
      webLandingURL: window.location.href
    };
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Crestmont Program Lead] payload:', payload);
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'crestmont-program-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    const postBase = university?.leadPostUrl || '';
    if (postBase) {
      const fullUrl = `${postBase}?${new URLSearchParams(payload).toString()}`;
      console.log('[Crestmont Program Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Crestmont Program Lead] post error:', err);
      }
    } else {
      console.log('[Crestmont Program Lead] No leadPostUrl configured — skipping post.');
    }
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      reportSubmission();
    }, 700);
  };

  // 16px type on mobile keeps iOS from zooming the viewport on focus; the
  // taller mobile control also gives a comfortable touch target.
  const inputClass = 'w-full px-3.5 py-3 sm:py-2.5 text-base sm:text-sm bg-white border border-gray-300 rounded text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#00426b] focus:ring-1 focus:ring-[#00426b] transition-colors';
  if (isSuccess) {
    return <div className="bg-white border border-gray-200 rounded-lg p-8 text-center" data-id="element-346" data-mp="6u5a91ue5pcx">
        <CheckCircleIcon className="w-10 h-10 mx-auto mb-4" style={{
        color: CRESTMONT.navy
      }} aria-hidden="true" data-id="element-347" data-mp="1w3qsft1zo0x" />
        <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-348" data-mp="3oan5kdlxx57x">
          Request received
        </h2>
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-349" data-mp="3jc3j8c3fprf1">
          A Crestmont advisor will reach out about the {programName} with program
          details, transfer credit, and next steps.
        </p>
      </div>;
  }
  return <form id="apply" onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-lg p-5 sm:p-6 md:p-7" noValidate data-id="element-350" data-mp="3acgd5czrfxfn">
      {/* Populated by the LeadiD and TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-351" data-mp="170quz16w8t9n" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-352" data-mp="t2irsbj9kvq1" />

      <h2 className="text-base sm:text-lg font-bold text-gray-900" data-id="element-353" data-mp="x3cex2ovu6hv">
        {heading}
      </h2>
      <p className="mt-1.5 text-xs text-gray-500 leading-relaxed" data-id="element-354" data-mp="jbxwawyw07bc">
        {subheading || `Tell us where to reach you and an advisor will follow up about the ${programName}.`}
      </p>

      <div className="mt-4 sm:mt-5 space-y-3" data-id="element-355" data-mp="9drwckwn5b94">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-356" data-mp="9etbp1kowjwn">
          <div data-id="element-357" data-mp="ecla8efub49t">
            <label htmlFor="cu-first" className="sr-only" data-id="element-358" data-mp="ec37cyyo9rsx">
              First name
            </label>
            <input id="cu-first" type="text" autoComplete="given-name" placeholder="First name" value={form.firstName} onChange={e => update('firstName', e.target.value)} className={inputClass} data-id="element-359" data-mp="ctt44u117rl" />
          </div>
          <div data-id="element-360" data-mp="3cy8uovkw7hbk">
            <label htmlFor="cu-last" className="sr-only" data-id="element-361" data-mp="3cysekhu3sfs8">
              Last name
            </label>
            <input id="cu-last" type="text" autoComplete="family-name" placeholder="Last name" value={form.lastName} onChange={e => update('lastName', e.target.value)} className={inputClass} data-id="element-362" data-mp="3quckmcoqh3i8" />
          </div>
        </div>
        <div data-id="element-363" data-mp="eesis9nna441">
          <label htmlFor="cu-email" className="sr-only" data-id="element-364" data-mp="efbpeu7uh62r">
            Email address
          </label>
          <input id="cu-email" type="email" autoComplete="email" placeholder="Email address" value={form.email} onChange={e => update('email', e.target.value)} className={inputClass} data-id="element-365" data-mp="9fpdqp7nzxw2" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-366" data-mp="2y0rg5kxml3zm">
          <div data-id="element-367" data-mp="2y2cdfibbr116">
            <label htmlFor="cu-phone" className="sr-only" data-id="element-368" data-mp="2ta2nw3e4g2zf">
              Phone number
            </label>
            <input id="cu-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="Phone number" value={form.phone} onChange={e => update('phone', formatPhone(e.target.value))} className={inputClass} data-id="element-369" data-mp="2oa6ka59c6dke" />
          </div>
          <div data-id="element-370" data-mp="3c126mmv0wgk9">
            <label htmlFor="cu-zip" className="sr-only" data-id="element-371" data-mp="3byg26w3fih69">
              ZIP code
            </label>
            <input id="cu-zip" type="text" inputMode="numeric" autoComplete="postal-code" placeholder="ZIP code" value={form.zipCode} onChange={e => update('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))} className={inputClass} data-id="element-372" data-mp="3730pi7h5qrbf" />
          </div>
        </div>
        <div data-id="element-373" data-mp="b3qe1j9bemfp">
          <label htmlFor="cu-program" className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5" data-id="element-374" data-mp="b38diafmdm1z">
            Degree of interest
          </label>
          <select id="cu-program" value={form.program} onChange={e => update('program', e.target.value)} className={inputClass} data-id="element-375" data-mp="kuus8gr6239h">
            {programOptions.map(option => <option key={option.value} value={option.value} data-id="element-376" data-mp="twkivkd84y67">
                {option.label}
              </option>)}
          </select>
        </div>
      </div>

      {error && <p role="alert" className="mt-4 text-sm text-red-600" data-id="element-377" data-mp="13qkxqdyb5hs4">
          {error}
        </p>}

      <button type="submit" disabled={isSubmitting} className="mt-5 w-full py-3.5 sm:py-3 rounded text-sm font-bold transition-colors disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={{
      backgroundColor: CRESTMONT.gold,
      color: CRESTMONT.ink
    }} data-id="element-378" data-mp="30n1zb6pl9fvr">
        {isSubmitting ? 'Sending…' : 'Request Information'}
      </button>

      <p className="mt-3.5 text-[10px] leading-relaxed text-gray-500" data-id="element-379" data-mp="32he6lzn0q25m">
        {university?.tcpaText}
      </p>
    </form>;
}